import { ISessionUser } from "links/lib/types";

export interface ILevelAnimations {
  startXP: number;
  endXP: number;
  level: number;
  nextLevel: number;
  levelXPThreshold: number;
  nextLevelXPThreshold: number;
}

const SeasonLevelRequiredExperience = 210;

const calculateSeasonLevel = (
  seasonXP: number
): {
  currentLevel: number;
  currentLevelXPThreshold: number;
  nextLevelXPThreshold: number;
} => {
  let currentLevel = Math.floor(seasonXP / SeasonLevelRequiredExperience);

  // minimum level is 1
  currentLevel = currentLevel + 1;

  return {
    currentLevel,
    currentLevelXPThreshold: (currentLevel - 1) * SeasonLevelRequiredExperience,
    nextLevelXPThreshold: currentLevel * SeasonLevelRequiredExperience,
  };
};

export const useLevelAnimations = (
  student: ISessionUser
): Array<ILevelAnimations> => {
  const levelAnimationItems: Array<ILevelAnimations> = [];

  const { session_start_xp, session_xp_total } = student;

  let currentXP = session_start_xp;
  let remainingGainedXP = session_xp_total;

  const currentSeasonLevelMetrics = calculateSeasonLevel(session_start_xp);

  if (session_xp_total === 0) {
    levelAnimationItems.push({
      startXP: session_start_xp,
      endXP: session_start_xp,
      level: currentSeasonLevelMetrics.currentLevel,
      levelXPThreshold: currentSeasonLevelMetrics.currentLevelXPThreshold,
      nextLevel: currentSeasonLevelMetrics.currentLevel + 1,
      nextLevelXPThreshold: currentSeasonLevelMetrics.nextLevelXPThreshold,
    });

    return levelAnimationItems;
  }

  let nextLevelThreshold = currentSeasonLevelMetrics.nextLevelXPThreshold;
  let levelXPThreshold = currentSeasonLevelMetrics.currentLevelXPThreshold;
  let currentLevel = currentSeasonLevelMetrics.currentLevel;
  let nextLevel = currentLevel + 1;

  while (remainingGainedXP > 0) {
    if (nextLevelThreshold <= currentXP + remainingGainedXP) {
      remainingGainedXP = currentXP + remainingGainedXP - nextLevelThreshold;

      levelAnimationItems.push({
        startXP: currentXP,
        endXP: nextLevelThreshold,
        level: currentLevel,
        nextLevel: currentLevel + 1,
        nextLevelXPThreshold: nextLevelThreshold,
        levelXPThreshold,
      });

      currentXP = nextLevelThreshold;
      currentLevel = nextLevel;
      nextLevel = nextLevel + 1;
      levelXPThreshold = nextLevelThreshold;
      const prevNextLevelThreshold = nextLevelThreshold;
      nextLevelThreshold = nextLevelThreshold + SeasonLevelRequiredExperience;

      // Case when XP gained puts user on the border between two levels.
      // Consider the level gained
      if (remainingGainedXP === 0 && currentXP === prevNextLevelThreshold) {
        levelAnimationItems.push({
          startXP: currentXP,
          endXP: currentXP,
          level: currentLevel,
          nextLevel,
          nextLevelXPThreshold: nextLevelThreshold,
          levelXPThreshold: currentXP,
        });
      }
    } else {
      levelAnimationItems.push({
        startXP: currentXP,
        endXP: currentXP + remainingGainedXP,
        level: currentLevel,
        nextLevel,
        nextLevelXPThreshold: nextLevelThreshold,
        levelXPThreshold,
      });

      remainingGainedXP = 0;
    }
  }

  return levelAnimationItems;
};
